import React, { useState, useEffect, useRef, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github.css";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import type { Citation, SearchDocument } from "@/types";
import "./index.css";
import PdfImage from "@/assets/search/pdf.svg?react";
import MdImage from "@/assets/search/ic-MarkDown.svg?react";
import { visit } from "unist-util-visit";

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import remarkMath from "remark-math";
import remarkBreaks from "remark-breaks";
import eventBus from "@/utils/eventBus.ts";
import { useTranslation } from "@/locales";
import { aiApi } from "@/api/aiApi";
import { getFileStream } from "@/api/client";
import { useIsMobile } from "@/hooks/use-mobile";

// 新增 ProposalCard 组件相关类型
interface ProposalData {
  proposalId: string;
  productCode: string;
  lang: string;
  currency: string;
  term: number;
  amount: number;
  age: number;
  gender: string;
  smoke: boolean;
  nationality: string;
  state: string;
  createdAt: string;
}

interface ProposalCardProps {
  proposalId: string;
}

const ProposalCard: React.FC<ProposalCardProps> = ({ proposalId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<ProposalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // 获取方案数据
  const fetchProposal = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await aiApi.conversation.getProposal(proposalId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "获取方案数据失败");
    } finally {
      setLoading(false);
    }
  };

  // 轮询刷新数据（仅在数据未完成时）
  const refreshData = async () => {
    if (data?.state === "FINISHED") return; // 如果已完成，不再轮询

    try {
      const response = await aiApi.conversation.getProposal(proposalId);
      setData(response);
    } catch (err) {
      console.warn("轮询刷新失败:", err);
    }
  };

  // 下载文件
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await getFileStream("/test/proposal/download?proposalId=" + proposalId);
      console.log("文件 Blob:", blob);
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `proposal_${proposalId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("下载失败:", err);
    } finally {
      setDownloading(false);
    }
  };

  // 初始化数据获取
  useEffect(() => {
    if (proposalId) {
      fetchProposal();
    }
  }, [proposalId]);

  // 智能轮询：只在需要时轮询
  useEffect(() => {
    if (!data || data.state === "FINISHED") return;

    const interval = setInterval(async () => {
      try {
        const response = await aiApi.getProposal(proposalId);
        if (response.success && response.data) {
          setData(response.data);
          if (response.data.state === "FINISHED") {
            clearInterval(interval);
          }
        }
      } catch (err) {
        console.warn("轮询刷新失败:", err);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [proposalId, data?.state]);

  if (loading && !data) {
    return (
      <div className="proposal-card border rounded-lg p-4 my-4 bg-white shadow-sm">
        <div className="animate-pulse">
          {/* 标题和按钮骨架 */}
          <div className="flex justify-between items-start mb-3">
            <div className="h-5 bg-gray-200 rounded w-20"></div>
            <div className="h-7 bg-gray-200 rounded w-12"></div>
          </div>

          {/* 内容骨架 - 模拟网格布局 */}
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="h-3 bg-gray-200 rounded w-16 flex-shrink-0"></div>
                <div className="h-3 bg-gray-200 rounded flex-1"></div>
              </div>
            ))}
          </div>

          {/* 加载提示 */}
          <div className="mt-3 flex items-center justify-center text-gray-500 text-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
            正在加载方案数据...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="proposal-card border rounded-lg p-4 my-4 bg-red-50 border-red-200">
        <div className="text-red-600 text-sm">
          {t("status.error")}: {error}
        </div>
        <button onClick={fetchProposal} className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200">
          {t("admin.retry")}
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="proposal-card border rounded-lg p-4 my-4 bg-white shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div className="text-lg font-semibold text-gray-800">{t("proposal.plan")}</div>
        <button onClick={handleDownload} disabled={downloading} className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
          {downloading ? t("file.downloading") : t("proposal.download")}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-gray-600">{t("proposal.proposalId")}:</span>
          <span className="ml-2 font-mono text-xs">{data.proposalId}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.productCode")}:</span>
          <span className="ml-2">{data.productCode}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.language")}:</span>
          <span className="ml-2">{data.lang}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.currency")}:</span>
          <span className="ml-2">{data.currency}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.term")}:</span>
          <span className="ml-2">{data.term} 年</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.amount")}:</span>
          <span className="ml-2">
            {data.amount.toLocaleString()} {data.currency}
          </span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.age")}:</span>
          <span className="ml-2">
            {data.age} {t("proposal.ageUnit")}
          </span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.gender")}:</span>
          <span className="ml-2">{data.gender === "MALE" ? t("proposal.male") : t("proposal.female")}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.smoke")}:</span>
          <span className="ml-2">{data.smoke ? t("proposal.yes") : t("proposal.no")}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.nationality")}:</span>
          <span className="ml-2">{data.nationality}</span>
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.status")}:</span>
          <span className={`ml-2 px-2 py-1 rounded text-xs ${data.state === "FINISHED" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
            {data.state === "FINISHED" ? t("validation.completed") : t("validation.processing")}
          </span>
          {data.state !== "FINISHED" && (
            <span className="ml-2 text-xs text-gray-500">
              <div className="inline-block animate-spin rounded-full h-3 w-3 border-b border-yellow-500"></div>
              <span className="ml-1">{t("validation.processing")}...</span>
            </span>
          )}
        </div>
        <div>
          <span className="text-gray-600">{t("proposal.createTime")}:</span>
          <span className="ml-2 text-xs">{new Date(data.createdAt).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

interface TypewriterMarkdownProps {
  content: string;
  speed?: number;
  onComplete?: () => void;
  className?: string;
  showCursor?: boolean;
  showSkipButton?: boolean;
  skipButtonText?: string;
  skipButtonClass?: string;
  renderer?: (content: string) => ReactNode;
  onCharacterTyped?: (char: string, index: number) => void;
  citations?: Citation[];
  docs?: SearchDocument[];
  isLatestMessage?: boolean;
  onPDFClick?: (citation?: Citation) => void;
  onUnderway?: (num?: number) => void;
  onUnderAnswer?: (num?: number) => void;
  useHtml?: boolean;
  think?: boolean;
  isEnd?: boolean;
  conversationNo?: string;
}

const TypewriterMarkdown = React.memo<TypewriterMarkdownProps>(
  ({
    content,
    speed = 10,
    onComplete,
    className = "",
    renderer,
    onCharacterTyped,
    citations = [],
    docs = [],
    isLatestMessage = false,
    onPDFClick,
    onUnderway,
    onUnderAnswer,
    useHtml = false,
    think = false,
    isEnd = false,
    conversationNo = null,
  }) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const [displayedContent, setDisplayedContent] = useState("");
    const indexRef = useRef(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (think) {
        onUnderway?.(displayedContent.length);
      }
      if (!think) {
        onUnderAnswer?.(displayedContent.length);
      }

      if (!isLatestMessage || isEnd) {
        // 如果不是最新消息，直接显示全部内容
        setDisplayedContent(content);
        setTimeout(() => {
          onComplete?.();
        }, 1500);
        return;
      }
      if (content.length === 0) {
        setDisplayedContent("");
        indexRef.current = 0;
        setTimeout(() => {
          onComplete?.();
        }, 1500);
        return;
      }

      // 如果新content比已显示内容短，重置
      if (content.length < displayedContent.length) {
        setDisplayedContent("");
        indexRef.current = 0;
      }

      // 继续从上次index开始打字
      const typeNextCharacter = () => {
        if (indexRef.current < content.length) {
          const nextChar = content.charAt(indexRef.current);

          // 处理代码块 - 一次性显示整个代码块
          if (nextChar === "`" && content.substring(indexRef.current).startsWith("```")) {
            const endIndex = content.indexOf("```", indexRef.current + 3) + 3;
            if (endIndex > indexRef.current) {
              const codeBlock = content.substring(indexRef.current, endIndex);
              setDisplayedContent((prev) => prev + codeBlock);
              indexRef.current = endIndex;
            } else {
              setDisplayedContent((prev) => prev + nextChar);
              indexRef.current++;
            }
          }
          // 处理HTML标签 - 一次性显示整个标签
          // else if (nextChar === '<') {
          //     const endIndex = content.indexOf('>', indexRef.current);
          //     if (endIndex > indexRef.current) {
          //         const htmlTag = content.substring(indexRef.current, endIndex + 1);
          //         setDisplayedContent(prev => prev + htmlTag);
          //         indexRef.current = endIndex + 1;
          //     } else {
          //         setDisplayedContent(prev => prev + nextChar);
          //         indexRef.current++;
          //     }
          // }
          // 正常字符
          else {
            setDisplayedContent((prev) => prev + nextChar);
            indexRef.current++;
          }

          onCharacterTyped?.(nextChar, indexRef.current);

          if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }

          timerRef.current = setTimeout(typeNextCharacter, speed);
        } else {
          setTimeout(() => {
            onComplete?.();
          }, 1500);
        }
      };

      if (!timerRef.current) {
        timerRef.current = setTimeout(typeNextCharacter, speed);
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }, [content, speed, onComplete, onCharacterTyped, displayedContent.length, isLatestMessage]);

    const preprocessContent = (text: string): string => {
      // 首先处理 bee-proposal 标签
      const proposalProcessed = text.replace(/<bee-proposal>([0-9a-fA-F-]{36})<\/bee-proposal>/g, "**BEE_PROPOSAL_START_$1_BEE_PROPOSAL_END**");

      // 然后处理其他格式，但要保护 bee-proposal 特殊标记
      return proposalProcessed
        .replace(/(\|-+)\s*\n\s*\|/g, "$1|") // 修复表格分隔线
        .replace(/^(#{1,6})(\s*)\*\*(.+?)\*\*/gm, "$1$2$3") // 修复标题中的加粗标记：###**标题** -> ### 标题
        .replace(/^(#{1,6}\s+[^*\n]*)\*\*(.+?)\*\*/gm, "$1$2") // 移除标题内容中的加粗标记
        .replace(/\*\*(?!BEE_PROPOSAL_START_)(.+?)(?<!_BEE_PROPOSAL_END)\*\*/g, "$1"); // 移除加粗标记，但保护 bee-proposal 特殊标记
    };

    const handleClick = (citation?: Citation, doc?: SearchDocument) => {
        eventBus.emit("pdfClick", {
          citation,
          doc,
          conversationNo: conversationNo,
        });
    };
    const renderCitation = (id: number, Cname: string) => {
      const citation = citations.find((c) => c.index === id);
      const doc = docs.find((d) => d.fileId === citation?.fileId);

      // 移动端不需要hover卡，直接点击，不然会报错
      if (isMobile) {
        return (
          <span
            key={`citation-${id}-${Cname}`}
            className="citation cursor-pointer"
            onClick={() => handleClick(citation, doc)}
          >
            {id}
          </span>
        );
      }

      return (
        <HoverCard key={`citation-${id}-${citation?.fileId}-${Cname}-${Math.random()}`}>
          <HoverCardTrigger>
            <span className="citation cursor-pointer" onClick={() => handleClick(citation, doc)}>
              {id}
            </span>
          </HoverCardTrigger>
          <HoverCardContent className="max-w-72 w-full p-2 cursor-pointer" onClick={() => handleClick(citation, doc)}>
            {doc ? (
              <div className="citation-content">
                <div className='flex items-start border-b  border-[#F4F4F5] pb-1.5"'>
                  <div className="w-[16px] h-[16px]">{doc?.type == "PDF" ? <PdfImage className="w-[16px] h-[16px] mr-1" /> : <MdImage className="w-[16px] h-[16px] mr-1" />}</div>
                  <div className="text-[12px] text-[var(--text-text_primary)] font-medium truncate-1-lines">{doc?.fileName}</div>
                </div>
                <div className="text-[#6A6A6A] truncate-3-lines text-xs mt-1.5">{citation?.summary || ""}</div>
              </div>
            ) : (
              <div className="citation-content">
                <div className="text-[#6A6A6A] truncate-3-lines text-xs mt-1.5">文档已经失效</div>
              </div>
            )}
          </HoverCardContent>
        </HoverCard>
      );
    };

    // 渲染文件ID标记
    const renderFileId = (ids: string[]) => {
      // const doc = docs.find(d => d.fileId === id);
      return (
        <span key={`file-multi-${ids.length}`} className="file-multi-info" onClick={handleClick}>
          <span className="file-text py-[3px] px-[8px] cursor-pointer">
            {ids.length}&nbsp; {t("status.data_items")} {">"}
          </span>
        </span>
      );
    };
    // 在 TypewriterMarkdown 组件内部，新增一个函数，预处理 content
    const safeProcessNodes = (nodes: React.ReactNode): React.ReactNode => {
      // 如果是纯字符串，直接处理
      if (typeof nodes === "string") {
        // 首先处理引用标记
        const citationProcessed = processAllMarkdown(nodes);
        // 然后处理文件ID
        return citationProcessed;
      }

      // 如果是节点数组，遍历处理每个元素
      if (Array.isArray(nodes)) {
        return nodes.map((node) => {
          if (typeof node === "string") {
            const citationProcessed = processAllMarkdown(node);
            return citationProcessed;
          }
          // 如果不是字符串节点，递归处理子节点
          return safeProcessNodes(node);
        });
      }

      // 如果是 React 元素且有子节点，克隆元素并处理其子节点
      if (React.isValidElement(nodes) && nodes.props.children) {
        // 定义HTML标准属性白名单
        const htmlAttributes = [
          "className",
          "id",
          "style",
          "title",
          "lang",
          "dir",
          "tabIndex",
          "role",
          "aria-label",
          "aria-labelledby",
          "aria-describedby",
          "data-state",
          "data-slot",
          "href",
          "target",
          "rel",
          "src",
          "alt",
          "width",
          "height",
          "onClick",
          "onMouseEnter",
          "onMouseLeave",
        ];

        // 只保留HTML标准属性
        const validProps = Object.keys(nodes.props)
          .filter((key) => key !== "children" && htmlAttributes.includes(key))
          .reduce((obj, key) => {
            obj[key] = nodes.props[key];
            return obj;
          }, {} as any);

        return React.cloneElement(nodes, {
          ...validProps,
          children: safeProcessNodes(nodes.props.children),
        });
      }

      // 其他类型的节点直接返回
      return nodes;
    };
    // 处理 [^数字]
    // const processCitation = (text: string): React.ReactNode[] => {
    //     const result: React.ReactNode[] = [];
    //     let lastIndex = 0, boldLastIndex = 0;
    //     const regex = /\[\^(\d+)\]/g;
    //     const boldRegex = /\*\*(.*?)\*\*/g;
    //     let match, boldMatch;
    //     while ((match = regex.exec(text)) !== null) {
    //         if (match.index > lastIndex) {
    //             result.push(text.substring(lastIndex, match.index));
    //         }
    //         const citationId = parseInt(match[1], 10);
    //         result.push(renderCitation(citationId, match.index.toString()));
    //         lastIndex = regex.lastIndex;
    //     }
    //     if (lastIndex < text.length) {
    //         result.push(text.substring(lastIndex));
    //     }
    //     while ((boldMatch = boldRegex.exec(text)) !== null) {
    //         if (boldMatch.index > boldLastIndex) {
    //             result.push(text.substring(boldLastIndex, boldMatch.index));
    //         }
    //         result.push(<strong>{boldMatch[1]}</strong>);
    //         boldLastIndex = boldRegex.lastIndex;
    //     }
    //
    //     return result;
    // };
    const processAllMarkdown = (text: string): React.ReactNode[] => {
      const result: React.ReactNode[] = [];
      let lastIndex = 0;

      // console.log(text)
      // 匹配所有特殊标记的模式数组
      const patterns = [
        // 引用标记 [^数字]
        {
          regex: /\[\^(\d+)\]/g,
          handler: (match: RegExpExecArray) => {
            const citationId = parseInt(match[1], 10);
            return renderCitation(citationId, match.index.toString());
          },
        },
        // 加粗标记 **文本**
        {
          regex: /\*\*(.*?)\*\*/g,
          handler: (match: RegExpExecArray) => <strong key={`strong-${match.index}`}>{match[1]}</strong>,
        },
        // 文件ID标记 [fileId:数字]
        {
          regex: /\[fileId:([\d,]+)\]/g,
          handler: (match: RegExpExecArray) => {
            const ids = match[1].split(",").map((idStr) => idStr.trim());
            return renderFileId(ids);
          },
        },
        // 新增：bee-proposal 标记（处理预处理后的特殊标记）
        {
          regex: /\*\*BEE_PROPOSAL_START_([0-9a-fA-F-]{36})_BEE_PROPOSAL_END\*\*/g,
          handler: (match: RegExpExecArray) => {
            const proposalId = match[1];
            return <ProposalCard key={`proposal-${proposalId}`} proposalId={proposalId} />;
          },
        },
        // 可以添加更多标记处理
      ];

      // 创建一个匹配数组并按位置排序
      const allMatches: {
        index: number;
        length: number;
        node: React.ReactNode;
      }[] = [];

      // 找到所有匹配项
      patterns.forEach((pattern) => {
        const regex = new RegExp(pattern.regex.source, "g");
        let match;
        while ((match = regex.exec(text)) !== null) {
          allMatches.push({
            index: match.index,
            length: match[0].length,
            node: pattern.handler(match),
          });
        }
      });

      // 按位置排序
      allMatches.sort((a, b) => a.index - b.index);

      // 处理文本并替换标记
      allMatches.forEach((match) => {
        if (match.index > lastIndex) {
          // 添加普通文本
          result.push(text.substring(lastIndex, match.index));
        }

        // 添加特殊节点
        result.push(match.node);

        // 更新最后索引
        lastIndex = match.index + match.length;
      });

      // 添加剩余文本
      if (lastIndex < text.length) {
        result.push(text.substring(lastIndex));
      }

      return result;
    };

    const flattenChildrenToString = (nodes: React.ReactNode): string => {
      if (typeof nodes === "string") return nodes;
      if (Array.isArray(nodes)) return nodes.map(flattenChildrenToString).join("");
      if (React.isValidElement(nodes) && nodes.props.children) {
        return flattenChildrenToString(nodes.props.children);
      }
      return "";
    };
    const debugTextRenderer = ({ children, ...props }) => {
      // console.log("Text node:", children);
      return <span {...props}>{children}</span>;
    };

    const FootnotePreserver = () => (tree) => {
      visit(tree, ["footnoteReference", "footnoteDefinition"], (node) => {
        // 处理脚注引用
        if (node.type === "footnoteReference") {
          node.type = "html";
          // 保留原始脚注标记 [] 并在后面添加空格
          node.value = `[^${node.identifier}]`;
        }

        // 完全移除脚注定义部分
        if (node.type === "footnoteDefinition") {
          node.type = "html";
          node.value = "";
        }
      });
    };

    return (
      <div className={`typewriter-markdown ${className}`} ref={containerRef}>
        {renderer ? (
          renderer(displayedContent)
        ) : useHtml ? (
          // 直接渲染HTML，注意安全性，确保content是可信的
          <div dangerouslySetInnerHTML={{ __html: displayedContent }} style={{ whiteSpace: "break-spaces" }} />
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkMath, remarkGfm, remarkBreaks, FootnotePreserver]}
            // rehypePlugins={[rehypeHighlight]}
            rehypePlugins={[
              rehypeKatex,
              rehypeRaw,
              [
                rehypeHighlight,
                {
                  detect: false,
                  ignoreMissing: true,
                },
              ],
            ]}
            components={{
              footer: () => null,
              a: ({ children, ...props }) => {
                // const href = props.href || "";
                // const isInternal = /^\/#/i.test(href);
                // const target = isInternal ? "_self" : props.target ?? "_blank";
                const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                };
                const processedChildren = safeProcessNodes(children);

                return (
                  <span {...props} className={"cursor-auto"} onClick={handleClick}>
                    {processedChildren}
                  </span>
                );
              },
              // ol({children}: any) {
              //     return <ol>{children}</ol>
              // },
              li({ children, ...props }) {
                // const fullText = flattenChildrenToString(children);
                // const citationProcessed = processCitation(fullText);
                // const finalProcessed = citationProcessed.flatMap(part => {
                //     if (typeof part === 'string') {
                //         return processFileId(part);
                //     }
                //     return part;
                // });
                const processedChildren = safeProcessNodes(children);
                return <li {...props}>{processedChildren}</li>;
                // 递归渲染子节点，确保markdown语法生效
              },
              strong({ children, ...props }) {
                // 检查是否是 bee-proposal 特殊标记
                const childText = typeof children === "string" ? children : Array.isArray(children) ? children.join("") : String(children);

                if (childText.match(/^BEE_PROPOSAL_START_[0-9a-fA-F-]{36}_BEE_PROPOSAL_END$/)) {
                  // 如果是 bee-proposal 标记，直接渲染为 ProposalCard
                  const match = childText.match(/^BEE_PROPOSAL_START_([0-9a-fA-F-]{36})_BEE_PROPOSAL_END$/);
                  if (match) {
                    const proposalId = match[1];
                    return <ProposalCard key={`proposal-${proposalId}`} proposalId={proposalId} />;
                  }
                }

                return <strong {...props}>{children}</strong>;
              },
              tr({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <tr {...props}>{processedChildren}</tr>;
              },

              td({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <td {...props}>{processedChildren}</td>;
              },
              h1({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <h1 {...props}>{processedChildren}</h1>;
              },
              h2({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <h2 {...props}>{processedChildren}</h2>;
              },
              h3({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <h3 {...props}>{processedChildren}</h3>;
              },
              h4({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <h4 {...props}>{processedChildren}</h4>;
              },
              h5({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <h5 {...props}>{processedChildren}</h5>;
              },
              h6({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <h6 {...props}>{processedChildren}</h6>;
              },
              hr: ({ node, ...props }) => (
                <hr
                  style={{
                    color: "#171A1D66",
                    height: "1px",
                    margin: "10px 0",
                  }}
                >
                  {props.children}
                </hr>
              ),

              code({ inline, className, children, ...props }: any) {
                // console.log("inline", inline);
                return (
                  <code className={`inline-code ${className}`} {...props}>
                    {children}
                  </code>
                );
                // const match = /language-(\w+)/.exec(className || '');
                // return inline ? (
                //     <SyntaxHighlighter
                //         style={vscDarkPlus}
                //         language={match ? match[1] : 'text'}
                //         PreTag="div"
                //         customStyle={{
                //             background: 'var(--widget-btn_hover_1)',
                //             borderRadius: '4px',
                //             padding: '1em',
                //             margin: '1em 0',
                //             overflowX: 'auto'
                //         }}
                //         codeTagProps={{style: {fontFamily: 'Fira Code, monospace'}}}
                //         {...props}
                //     >
                //         {String(children).replace(/\n$/, '')}
                //     </SyntaxHighlighter>
                // ) : (
                //     <code className={`inline-code ${className}`} {...props}>
                //         {children}
                //     </code>
                // );
              },
              // 新增对 fileId 标签的处理
              // 修正后的处理函数
              p({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <p {...props}>{processedChildren}</p>;
              },
              div({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <div {...props}>{processedChildren}</div>;
              },
              span({ children, ...props }) {
                const processedChildren = safeProcessNodes(children);
                return <span {...props}>{processedChildren}</span>;
              },
              text({ children, ...props }) {
                return <span {...props}>{children}</span>;
              },
            }}
            skipHtml={false}
          >
            {preprocessContent(displayedContent)}
          </ReactMarkdown>
        )}
      </div>
    );
  },
  // 自定义比较函数，只比较关键 props
  (prevProps, nextProps) => {
    return (
      prevProps.content === nextProps.content &&
      prevProps.conversationNo === nextProps.conversationNo &&
      prevProps.speed === nextProps.speed &&
      JSON.stringify(prevProps.citations) === JSON.stringify(nextProps.citations) &&
      JSON.stringify(prevProps.docs) === JSON.stringify(nextProps.docs)
    );
  }
);

export default TypewriterMarkdown;
